import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { dataLayerPush } from '@libs/analytics';
import { isLocaleCA } from '@libs/core';

import { useProduct } from '@/entities/product';
import type { FormFields } from '@/entities/purchase-form';
import { useFulfillmentAnalyticsStore } from '@/shared/analytics/model/fulfillment-analytics.store';

export function useChangeDeliveryMethodAnalytics(form: UseFormReturn<FormFields>) {
    const { watch, setValue } = form;
    const { product } = useProduct();

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevValuesRef = useRef<Pick<FormFields, 'purchaseOption' | 'fulfilmentMethod'>>({
        purchaseOption: form.getValues('purchaseOption'),
        fulfilmentMethod: form.getValues('fulfilmentMethod'),
    });

    useEffect(() => {
        const subscription = watch((currentValues, { name }) => {
            if (name !== 'purchaseOption' && name !== 'fulfilmentMethod') {
                return;
            }

            const oldPurchaseOption = prevValuesRef.current.purchaseOption;
            const oldFulfillmentMethod = prevValuesRef.current.fulfilmentMethod;
            const newPurchaseOption = currentValues.purchaseOption;
            const newFulfillmentMethod = currentValues.fulfilmentMethod;

            if (!newPurchaseOption || !newFulfillmentMethod) {
                return;
            }

            if (useFulfillmentAnalyticsStore.getState().isSuppressed) {
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                }

                prevValuesRef.current = {
                    purchaseOption: newPurchaseOption,
                    fulfilmentMethod: newFulfillmentMethod,
                };
                return;
            }

            const oldSelection = `${oldFulfillmentMethod} ${oldPurchaseOption}` as const;
            const newSelection = `${newFulfillmentMethod} ${newPurchaseOption}` as const;

            if (!product || oldSelection === newSelection) {
                return;
            }

            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(() => {
                const priceToUse = product.price.sale ?? product.price.original ?? 0.0;
                const quantity = currentValues.quantity || 1;
                const value = priceToUse * quantity;
                dataLayerPush({
                    event: 'change_fulfillment_method',
                    value,
                    currency: isLocaleCA() ? 'CAD' : 'USD',
                    old_selection: oldSelection,
                    new_selection: newSelection,
                    product_id: product.sku,
                    product_price: priceToUse,
                    product_quantity: quantity,
                });

                prevValuesRef.current = {
                    purchaseOption: newPurchaseOption,
                    fulfilmentMethod: newFulfillmentMethod,
                };
            }, 100);
        });

        return () => {
            subscription.unsubscribe();
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [watch, setValue, product]);
}
